import { animate, motion, useMotionValue } from "framer-motion"
import { useLayoutEffect, useRef } from "react"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

/**
 * Подложка под выбранным пунктом переключателя. Ставится первым ребёнком дорожки
 * (у дорожки `relative`), выбранный пункт помечается `data-active="true"` или атрибутом
 * из `attribute`.
 *
 * Позицию считает внутри своей дорожки, а не на странице, поэтому не прилетает издалека,
 * когда страница сама въезжает или прокручена, и не перескакивает между разными
 * переключателями. При первом показе встаёт на место без движения.
 *
 * К другому пункту подложка едет пружиной. Если же пункт просто меняет размер (меню
 * сворачивается, проявляется счётчик), она повторяет его в том же кадре: значения пишутся
 * прямо в стиль, мимо перерисовки React и кадра framer-motion, которые отставали бы.
 */
export function SlidingPill({
	className,
	attribute = "data-active",
}: {
	className?: string
	/**
	 * Атрибут выбранного пункта. По умолчанию `data-active="true"`; для меню Radix это
	 * `data-highlighted`, который он ставит пункту под курсором или фокусом.
	 */
	attribute?: string
}) {
	const selector = attribute === "data-active" ? '[data-active="true"]' : `[${attribute}]`
	const ref = useRef<HTMLSpanElement>(null)
	const x = useMotionValue(0)
	const y = useMotionValue(0)
	const width = useMotionValue(0)
	const height = useMotionValue(0)
	const opacity = useMotionValue(0)

	useLayoutEffect(() => {
		const track = ref.current?.parentElement
		if (!track) return
		let current: HTMLElement | null = null
		let traveling = false
		let controls: Array<{ stop: () => void }> = []

		const measure = () => {
			const active = track.querySelector<HTMLElement>(selector)
			if (!active) {
				current = null
				opacity.set(0)
				return
			}
			// На узком экране дорожка прокручивается: выбранный пункт не должен прятаться за краем.
			if (track.scrollWidth > track.clientWidth) {
				const start = active.offsetLeft - 8
				const end = active.offsetLeft + active.offsetWidth + 8
				if (start < track.scrollLeft) track.scrollTo({ left: start, behavior: "smooth" })
				else if (end > track.scrollLeft + track.clientWidth)
					track.scrollTo({ left: end - track.clientWidth, behavior: "smooth" })
			}
			const target: Array<[typeof x, number]> = [
				[x, active.offsetLeft],
				[y, active.offsetTop],
				[width, active.offsetWidth],
				[height, active.offsetHeight],
			]
			const travel = current !== null && active !== current
			current = active
			opacity.set(1)
			if (travel || traveling) {
				for (const control of controls) control.stop()
				traveling = true
				const started = target.map(([value, to]) => animate(value, to, TRANSITION.spring))
				controls = started
				void Promise.all(started).then(() => {
					if (controls === started) traveling = false
				})
				return
			}
			for (const [value, to] of target) value.jump(to)
			// framer-motion применит значения только в следующем кадре, а здесь важен этот.
			const pill = ref.current
			if (pill) {
				pill.style.transform = `translateX(${active.offsetLeft}px) translateY(${active.offsetTop}px)`
				pill.style.width = `${active.offsetWidth}px`
				pill.style.height = `${active.offsetHeight}px`
				pill.style.opacity = "1"
			}
		}

		measure()
		let frame = 0
		const schedule = () => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(measure)
		}
		// Колбэк ResizeObserver идёт после раскладки и до отрисовки, поэтому новое значение
		// попадает в тот же кадр. Сама подложка не наблюдается, так что цикла нет.
		const resize = new ResizeObserver(measure)
		const observeChildren = () => {
			resize.disconnect()
			resize.observe(track)
			for (const child of track.children) if (child !== ref.current) resize.observe(child)
		}
		observeChildren()
		const mutation = new MutationObserver((records) => {
			if (records.some((record) => record.type === "childList" && record.target === track))
				observeChildren()
			schedule()
		})
		mutation.observe(track, {
			subtree: true,
			childList: true,
			characterData: true,
			attributes: true,
			attributeFilter: [attribute],
		})
		return () => {
			cancelAnimationFrame(frame)
			for (const control of controls) control.stop()
			resize.disconnect()
			mutation.disconnect()
		}
	}, [x, y, width, height, opacity, selector, attribute])

	return (
		<motion.span
			ref={ref}
			aria-hidden
			style={{ x, y, width, height, opacity }}
			className={cn("pointer-events-none absolute top-0 left-0 bg-background shadow-sm", className)}
		/>
	)
}
